/**
 * 素材来源数据增强器
 * 优先级：3（在 NavigationEnricher 和 DOMEnricher 之后）
 * 职责：为 material_click 和 content_expose 事件自动生成 pin_source 字段
 *
 * ⚠️ 重要：pin_source 生成规则必须与旧版完全一致，因为涉及算法的离线计算
 * 参考：tracker/collecter/materialClick.js 和 tracker/collecter/expose.js
 *
 * pin_source 格式（与旧版完全一致）：
 * - 有 source: `${page_nm}_${sub_page_nm || ""}_${filter_one[0]}_${source}` 或 `${page_nm}_${sub_page_nm || ""}_${source}`
 * - 无 source: `${page_nm}_${sub_page_nm}_${filter_one[0]}` 或 `${page_nm}_${sub_page_nm}` 或 `${page_nm}`
 *
 * 特殊处理：
 * - 发现页 + module_name="画板信息"：追加 `_画板信息` 到 pin_source
 */

import Router from "next/router";
import { PinDetailContext } from "../context/PinDetailContext";
import { DataEnricher, EnrichContext } from "./types";

export class MaterialSourceEnricher implements DataEnricher {
  name = "MaterialSourceEnricher";
  priority = 3; // 在 NavigationEnricher (1) 和 DOMEnricher (2) 之后

  shouldEnrich(context: EnrichContext): boolean {
    // 在 material_click 和 content_expose 事件时执行
    const eventName = context.trackData.event_name || context.trackData.event;
    return eventName === "material_click" || eventName === "content_expose";
  }

  enrich(context: EnrichContext): Record<string, any> {
    const { trackData, targetNode, page } = context;

    // 如果已经存在 pin_source，则不覆盖
    if (trackData.pin_source) {
      return trackData;
    }

    const { page_nm, sub_page_nm } = page;

    // 获取 source（完全按照旧版逻辑）
    // 旧版逻辑：从 DOM 获取 data-content-source（向上查找父元素）
    let source: string | undefined;

    if (targetNode) {
      source = this.getAttribute(targetNode, "data-content-source");
    }

    // 获取 filter（完全按照旧版逻辑，调用 getFilter 函数）
    const filter = this.getFilter(page_nm, targetNode);

    // 生成 pin_source（完全按照旧版逻辑）
    if (source) {
      // 有 source 的情况
      if (filter.filter_one && filter.filter_one.length > 0) {
        trackData.pin_source = `${page_nm}_${sub_page_nm || ""}_${
          filter.filter_one[0]
        }_${source}`;
      } else {
        trackData.pin_source = `${page_nm}_${sub_page_nm || ""}_${source}`;
      }
    } else {
      // 无 source 的情况
      if (filter.filter_one && filter.filter_one.length > 0) {
        trackData.pin_source = sub_page_nm
          ? `${page_nm}_${sub_page_nm}_${filter.filter_one[0]}`
          : `${page_nm}_${filter.filter_one[0]}`;
      } else {
        trackData.pin_source = sub_page_nm
          ? `${page_nm}_${sub_page_nm}`
          : page_nm;
      }
    }

    // 特殊处理：发现页 + module_name="画板信息"（完全按照旧版逻辑）
    if (page_nm === "发现页") {
      const moduleName =
        trackData.module_name ||
        (targetNode ? this.getAttribute(targetNode, "data-module-name") : null);
      if (moduleName === "画板信息") {
        trackData.pin_source = `${trackData.pin_source}_${moduleName}`;
      }
    }

    // ⚠️ 重要：将 filter_one 和 filter_two 添加到 trackData（与旧版逻辑一致）
    // 旧版逻辑：merge(trackData, filter) 会将 filter_one 和 filter_two 合并到 trackData
    if (filter.filter_one && filter.filter_one.length > 0) {
      trackData.filter_one = filter.filter_one;
    } else if (filter.filter_one === "") {
      trackData.filter_one = "";
    }

    if (filter.filter_two && filter.filter_two.length > 0) {
      trackData.filter_two = filter.filter_two;
    } else if (filter.filter_two === "") {
      trackData.filter_two = "";
    }

    // 开发环境日志
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("[MaterialSourceEnricher]:", {
        pin_source: trackData.pin_source,
        page_nm,
        sub_page_nm,
        source,
        filter,
      });
    }

    return trackData;
  }

  /**
   * 获取 DOM 属性（向上查找父元素）
   * 参考：tracker/collecter/get.js 中的 getAttribute 函数
   */
  private getAttribute(
    element: Element | null,
    attributeName: string
  ): string | undefined {
    if (!element) return undefined;

    // 先检查当前元素
    if (element.hasAttribute(attributeName)) {
      return element.getAttribute(attributeName) || undefined;
    }

    // 向上查找父元素
    let parent = element.parentElement;
    while (parent) {
      if (parent.hasAttribute(attributeName)) {
        return parent.getAttribute(attributeName) || undefined;
      }
      parent = parent.parentElement;
    }

    return undefined;
  }

  /**
   * 获取 filter 数据（完全按照旧版逻辑）
   * 参考：tracker/collecter/get.js 中的 getFilter 函数
   */
  private getFilter(
    page_nm: string,
    targetNode: Element | null
  ): { filter_one: string[] | ""; filter_two: string[] | "" } {
    // 完全按照旧版逻辑：从 DOM 获取 filter_one 和 filter_two
    let filter_one: string[] = [];
    let filter_two: string[] = [];

    // 完全按照旧版逻辑：从 DOM 获取 filter_one 和 filter_two
    // 旧版：const filter_one = getAttribute(targetNode, "data-filter-one") || [];
    // 注意：旧版的 getAttribute 返回字符串或 undefined，所以需要处理
    if (targetNode) {
      const filterOneAttr = this.getAttribute(targetNode, "data-filter-one");
      const filterTwoAttr = this.getAttribute(targetNode, "data-filter-two");

      // 完全按照旧版逻辑：如果 getAttribute 返回字符串，则作为数组的第一个元素
      if (filterOneAttr) {
        // 旧版的 getAttribute 返回字符串，直接 push 到数组
        filter_one.push(filterOneAttr);
      }

      if (filterTwoAttr) {
        filter_two.push(filterTwoAttr);
      }
    }

    // 发现页或 ai圈页面（完全按照旧版逻辑）
    if (page_nm === "发现页" || page_nm === "ai圈页面") {
      if (typeof window !== "undefined") {
        const activeFilter = document.getElementById("active-filter-item");
        if (!activeFilter) {
          return {
            filter_one: filter_one.length > 0 ? filter_one : [],
            filter_two: filter_two.length > 0 ? filter_two : [],
          };
        }
        filter_one.push(activeFilter.innerText);

        // 在发现页画板页面，需要获取画板ID（完全按照旧版逻辑）
        try {
          const { board } = Router.router?.query || {};
          if (board) {
            filter_two.push(board as string);
          }
        } catch (e) {
          // 静默失败
        }
      }
    }

    // 热点日历聚合页（完全按照旧版逻辑）
    if (page_nm === "热点日历聚合页") {
      if (typeof window !== "undefined") {
        const filterOne =
          document.getElementById("active-filter-one")?.innerText;
        const filterTwo =
          document.getElementById("active-filter-two")?.innerText;
        if (filterOne) {
          filter_one.push(filterOne);
        }
        if (filterTwo) {
          filter_two.push(filterTwo);
        }
      }
    }

    // 搜索结果页（完全按照旧版逻辑）
    if (page_nm === "搜索结果页") {
      try {
        // 完全按照旧版逻辑：使用 Router.router.query
        const { type, category } = Router.router?.query || {};

        switch (type) {
          case "board":
            filter_one.push("画板");
            break;
          case "user":
            filter_one.push("用户");
            break;
          case "pin":
            filter_one.push("采集");
            // 二级筛选项_分类信息（完全按照旧版逻辑）
            try {
              const useAppStore = require("@/context/store").default;
              const categories = useAppStore.getState()?.categories || [];
              const categoryName =
                categories.find((item: any) => item?.id === category)?.name ||
                "全部";
              filter_two.push(categoryName);
            } catch (e) {
              filter_two.push("全部");
            }
            break;
          case "material":
            filter_one.push("素材");
            break;
          case "aigc":
            filter_one.push("aigc");
            break;
          default:
            // 默认采集搜索情况（完全按照旧版逻辑）
            const activeTab = document.querySelector(".ant-tabs-tab-active");
            if (activeTab && activeTab.children[0]) {
              filter_one.push((activeTab.children[0] as HTMLElement).innerText);
              // 二级筛选项_分类信息
              try {
                const useAppStore = require("@/context/store").default;
                const categories = useAppStore.getState()?.categories || [];
                const categoryName =
                  categories.find((item: any) => item?.id === category)?.name ||
                  "全部";
                filter_two.push(categoryName);
              } catch (e) {
                filter_two.push("全部");
              }
            }
            break;
        }
      } catch (error) {
        // 静默失败，不影响主流程
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[MaterialSourceEnricher] Error getting filter for 搜索结果页:",
            error
          );
        }
      }
    }

    // 花瓣素材首页
    if (page_nm === "花瓣素材首页") {
      return {
        filter_one: [],
        filter_two: [],
      };
    }

    // 返回格式与旧版完全一致
    // 旧版：return { filter_one: filter_one.length ? filter_one : "", filter_two: filter_two.length ? filter_two : "" }
    return {
      filter_one: filter_one.length > 0 ? filter_one : ("" as ""),
      filter_two: filter_two.length > 0 ? filter_two : ("" as ""),
    };
  }
}
